// ABOUTME: Custom hooks for settings API operations (CRUD)
// ABOUTME: Provides hooks for system, company, user settings, and MCP instructions with loading/error states

import { useCallback, useEffect, useState } from 'react';

const API_BASE_URL = '/api/settings';

type SettingsCategory = 'system' | 'company' | 'user';

type SettingsData = Record<string, unknown>;

type SettingsState<T> = {
    data: T | null;
    loading: boolean;
    error: string | null;
};

type SettingsActions = {
    update: (data: SettingsData) => Promise<boolean>;
    reset: () => Promise<void>;
};

type UseSettingsResult<T> = SettingsState<T> & SettingsActions;

/**
 * Fetch settings from API
 */
const fetchSettings = async <T>(category: SettingsCategory): Promise<T> => {
    const response = await fetch(`${API_BASE_URL}/${category}`, {
        credentials: 'include',
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch settings');
    }

    const result = await response.json();
    return result.data || {};
};

/**
 * Update settings via API
 */
const updateSettings = async (category: SettingsCategory, data: SettingsData): Promise<boolean> => {
    const response = await fetch(`${API_BASE_URL}/${category}`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update settings');
    }

    return true;
};

/**
 * The modules that ship built-in object schemas. Mirrors `BuiltInModule` on the server:
 * aligning a module aligns every built-in schema registered for it.
 */
export type BuiltInModule =
    | 'prototype'
    | 'cpq'
    | 'crm'
    | 'variantPricing'
    | 'bom'
    | 'media'
    | 'pricing'
    | 'dealerships'
    | 'userManagement';

export type SchemaAlignStatus =
    | 'updated'
    | 'would-update'
    /** a tenant-package schema absent from the tenant, created (or to be created) by the run */
    | 'created'
    | 'would-create'
    | 'up-to-date'
    /** nothing left to apply, but the shipped definition is not fully represented on the tenant */
    | 'incomplete'
    | 'not-initialized'
    | 'skipped'
    | 'failed';

export type ContentTypeRef = {
    group: string;
    code: string;
};

/** A read filter shipped by the tenant package, as the server diffs it against the stored ones. */
export type ShippedReadFilter = {
    code: string;
    expression: string;
    timeoutMs?: number | null;
    description?: string | null;
};

export type ContentTypeFieldChange = {
    field: string;
    stored: unknown;
    shipped: unknown;
};

/**
 * The diff between the shipped definition of a built-in schema and the tenant's stored one.
 * Additive by construction: nothing here is ever a deletion or a rename.
 */
export type SchemaDiff = {
    code: string;
    objectSchemaId: string;
    missingGroups: string[];
    missingContentTypes: { group: string; code: string; type: string; mandatory: boolean }[];
    conflictingContentTypes: {
        group: string;
        code: string;
        shippedType: string;
        storedType: string;
    }[];
    driftedContentTypes: {
        group: string;
        code: string;
        changedFields: ContentTypeFieldChange[];
        wouldLose: string[];
    }[];
    rootPropertyChanges: { property: string; stored: unknown; shipped: unknown }[];
    extraGroups: string[];
    extraContentTypes: ContentTypeRef[];
    skippedForMissingDependency: { group: string; code: string; missingDependency: string }[];
    /** package read filters absent from the schema; always empty without a tenant package */
    missingReadFilters: ShippedReadFilter[];
    /** package read filters stored with another expression or timeout */
    driftedReadFilters: ShippedReadFilter[];
    /** core fields the tenant package supersedes and the schema still carries; empty without a package */
    supersededContentTypes: { group: string; code: string; reason: string }[];
    /** the hierarchy of a package schema stored without its `companyHierarchies` entry */
    missingHierarchyEntry?: string;
};

/**
 * Narrows an apply run to the diff entries the user ticked. The server recomputes the diff and
 * applies only the intersection, so a selection built from a stale preview can only shrink.
 */
export type AlignSelection = {
    [schemaCode: string]: {
        groups?: string[];
        contentTypes?: ContentTypeRef[];
        overwriteContentTypes?: ContentTypeRef[];
        rootProperties?: string[];
        /** codes of package read filters to write: missing ones restored, drifted ones replaced */
        readFilters?: string[];
        /** superseded fields to remove from the schema — destructive */
        removeContentTypes?: ContentTypeRef[];
        /** record the package schema's missing `companyHierarchies` entry */
        hierarchyEntry?: boolean;
    };
};

export type SchemaAlignResult = {
    module: BuiltInModule;
    code: string;
    status: SchemaAlignStatus;
    objectSchemaId?: string;
    diff?: SchemaDiff;
    createdGroups: string[];
    createdContentTypes: ContentTypeRef[];
    replacedContentTypes: ContentTypeRef[];
    appliedRootProperties: string[];
    appliedReadFilters: string[];
    removedContentTypes: ContentTypeRef[];
    /** the hierarchy recorded as the package schema's `companyHierarchies` entry */
    restoredHierarchyEntry?: string;
    withheldMandatoryContentTypes: ContentTypeRef[];
    ignoredSelections: { kind: string; value: string }[];
    error?: string;
};

export type AlignSchemasBody = {
    module?: BuiltInModule;
    codes?: string[];
    /** defaults to TRUE on the server — an unqualified call can never write */
    dryRun?: boolean;
    selection?: AlignSelection;
};

/**
 * Align the tenant's stored object schemas with the shipped built-in definitions (PIM_ADMIN only).
 *
 * Called exactly twice per operation: once with `dryRun: true` to build the diff, once with
 * `dryRun: false` and the selection the user confirmed.
 */
export const alignSchemas = async (body: AlignSchemasBody): Promise<SchemaAlignResult[]> => {
    const response = await fetch(`${API_BASE_URL}/schemas/align`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to align schemas');
    }

    const result = await response.json();
    return result.results || [];
};

/**
 * Fetch JSON schema for a settings category
 */
const fetchSchema = async (category: SettingsCategory): Promise<Record<string, unknown>> => {
    const response = await fetch(`${API_BASE_URL}/schema/${category}`, {
        credentials: 'include',
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to fetch schema');
    }

    const result = await response.json();
    return result.schema || {};
};

/**
 * Generic hook for settings management
 */
const useSettings = <T extends SettingsData>(category: SettingsCategory): UseSettingsResult<T> => {
    const [state, setState] = useState<SettingsState<T>>({
        data: null,
        loading: true,
        error: null,
    });

    const loadSettings = useCallback(async () => {
        try {
            setState(prev => ({ ...prev, loading: true, error: null }));
            const data = await fetchSettings<T>(category);
            setState({ data, loading: false, error: null });
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            setState({ data: null, loading: false, error: errorMessage });
        }
    }, [category]);

    const update = useCallback(
        async (data: SettingsData): Promise<boolean> => {
            try {
                await updateSettings(category, data);
                setState(prev => ({ ...prev, data: data as T }));
                return true;
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Unknown error';
                setState(prev => ({ ...prev, error: errorMessage }));
                return false;
            }
        },
        [category],
    );

    const reset = useCallback(async () => {
        await loadSettings();
    }, [loadSettings]);

    useEffect(() => {
        loadSettings();
    }, [loadSettings]);

    return {
        ...state,
        update,
        reset,
    };
};

/**
 * Hook for fetching settings schema
 */
export const useSettingsSchema = (category: SettingsCategory) => {
    const [schema, setSchema] = useState<Record<string, unknown> | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const loadSchema = async () => {
            try {
                setLoading(true);
                setError(null);
                const schemaData = await fetchSchema(category);
                setSchema(schemaData);
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Unknown error';
                setError(errorMessage);
            } finally {
                setLoading(false);
            }
        };

        loadSchema();
    }, [category]);

    return { schema, loading, error };
};

/**
 * Hook for system settings (PIM_ADMIN only)
 */
export const useSystemSettings = () => useSettings('system');

/**
 * Hook for company settings (PIM_ADMIN only)
 */
export const useCompanySettings = () => useSettings('company');

/**
 * Hook for user settings (all authenticated users)
 */
export const useUserSettings = () => useSettings('user');

/**
 * MCP instructions data shape returned by the API
 */
type McpInstructionsData = {
    content: string;
    lastModifiedAt: number | null;
    lastModifiedBy?: string;
};

/**
 * Hook for MCP instructions management (PIM_ADMIN only)
 */
export const useMcpInstructions = () => {
    const [data, setData] = useState<McpInstructionsData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const loadMcpInstructions = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch(`${API_BASE_URL}/mcp-instructions`, {
                credentials: 'include',
            });

            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.error || 'Failed to fetch MCP instructions');
            }

            const result = await response.json();
            setData(result.data || { content: '', lastModifiedAt: null });
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, []);

    const updateContent = useCallback(
        async (content: string): Promise<boolean> => {
            try {
                const response = await fetch(`${API_BASE_URL}/mcp-instructions`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    credentials: 'include',
                    body: JSON.stringify({ content }),
                });

                if (!response.ok) {
                    const err = await response.json();
                    throw new Error(err.error || 'Failed to update MCP instructions');
                }

                await loadMcpInstructions();
                return true;
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Unknown error';
                setError(errorMessage);
                return false;
            }
        },
        [loadMcpInstructions],
    );

    const reset = useCallback(async () => {
        await loadMcpInstructions();
    }, [loadMcpInstructions]);

    useEffect(() => {
        loadMcpInstructions();
    }, [loadMcpInstructions]);

    return {
        data,
        loading,
        error,
        updateContent,
        reset,
    };
};
